import { useFormikContext } from "formik";
import * as React from "react";
import {
  Field,
  FieldDescription,
  FieldError,
  FieldLabel,
} from "@volca/ui/components/ui/field";
import {
  InputGroup,
  InputGroupAddon,
  InputGroupInput,
  InputGroupText,
} from "@volca/ui/components/ui/input-group";
import { Skeleton } from "@volca/ui/components/ui/skeleton";
import { Stack } from "@volca/ui/components/ui/stack";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@volca/ui/components/ui/tooltip";
import { cn } from "@volca/ui/lib/utils";

type Adornment = React.ReactNode;

export interface FormInputProps extends React.ComponentProps<"input"> {
  name: string;
  label?: string;
  error?: string;
  showErrorMessage?: boolean;
  description?: React.ReactNode;
  info?: string;
  fullWidth?: boolean;
  RightActionComponent?: React.ReactNode;
  disableRequirementAsterisk?: boolean;
  startAdornment?: Adornment;
  endAdornment?: Adornment;
  disabledTooltip?: React.ReactNode;
  isLoading?: boolean;
  hideAutomaticError?: boolean;
  hideErrorMessage?: boolean;
}

const getNestedValue = (
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  obj: Record<string, any>,
  name: string,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
): any => {
  const parts = name.split(/[[\].]/).filter(Boolean);
  let current = obj;

  if (!current || Object.keys(current).length === 0) {
    return undefined;
  }

  for (const part of parts) {
    if (current === undefined || current === null) {
      return undefined;
    }
    current = current[part];
  }

  return current;
};

const renderAdornment = (adornment: Adornment) =>
  typeof adornment === "string" ? (
    <InputGroupText>{adornment}</InputGroupText>
  ) : (
    adornment
  );

const ADDON_CLASS = "[&>svg]:size-4";

const FormInput = React.forwardRef<HTMLInputElement, FormInputProps>(
  (
    {
      className,
      id,
      error,
      info,
      name,
      label,
      fullWidth,
      isLoading,
      hideAutomaticError = false,
      hideErrorMessage = false,
      showErrorMessage = true,
      description,
      RightActionComponent,
      required,
      disableRequirementAsterisk,
      placeholder,
      startAdornment,
      endAdornment,
      disabled,
      disabledTooltip,
      onChange,
      onInput,
      onBlur,
      ...props
    },
    ref,
  ) => {
    const generatedId = React.useId();
    const { errors } = useFormikContext<Record<string, unknown>>();
    const [hasTyped, setHasTyped] = React.useState(false);
    const [hasBlurred, setHasBlurred] = React.useState(false);

    if (isLoading) {
      return (
        <Stack gap={2} className={cn({ "w-full": fullWidth })}>
          {label && <Skeleton className="h-3.5 w-24" />}
          <Skeleton className="h-8 w-full" />
        </Stack>
      );
    }

    const formikError = !hideAutomaticError
      ? getNestedValue(errors, name)
      : undefined;
    const showFormikError =
      !hideAutomaticError &&
      hasTyped &&
      hasBlurred &&
      typeof formikError === "string" &&
      formikError.length > 0;

    const resolvedError = error || (showFormikError ? formikError : undefined);
    const resolvedDescription = error ? undefined : (description ?? info);
    const inputId = id ?? generatedId;
    const descriptionId = `${inputId}-description`;
    const errorId = `${inputId}-error`;
    const showAsterisk = Boolean(required && !disableRequirementAsterisk);
    const hasErrorMessage = Boolean(
      resolvedError && showErrorMessage && !hideErrorMessage,
    );
    const describedBy =
      [resolvedDescription && descriptionId, hasErrorMessage && errorId]
        .filter(Boolean)
        .join(" ") || undefined;
    const placeholderText =
      placeholder && showAsterisk && !label ? `${placeholder} *` : placeholder;

    const end = RightActionComponent ? (
      <>
        {endAdornment && renderAdornment(endAdornment)}
        <span className="flex items-center *:m-0 *:self-auto">
          {RightActionComponent}
        </span>
      </>
    ) : (
      endAdornment
    );

    const handleTypedStateUpdate = (value: string) => {
      if (!hasTyped && value.length > 0) {
        setHasTyped(true);
      }
    };

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      handleTypedStateUpdate(e.target.value);
      if (onChange) {
        onChange(e);
      }
    };

    const handleInput = (e: React.InputEvent<HTMLInputElement>) => {
      handleTypedStateUpdate(e.currentTarget.value);
      if (onInput) {
        onInput(e);
      }
    };

    const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
      if (e.target.value.length > 0) {
        setHasBlurred(true);
      }
      if (onBlur) {
        onBlur(e);
      }
    };

    const field = (
      <Field
        data-invalid={Boolean(resolvedError) || undefined}
        data-disabled={disabled || undefined}
        className={cn({ "w-auto": !fullWidth })}
      >
        {label && (
          <FieldLabel htmlFor={inputId} className="gap-0.5">
            {label}
            {showAsterisk && <span aria-hidden="true">*</span>}
          </FieldLabel>
        )}
        <InputGroup className={className}>
          {startAdornment && (
            <InputGroupAddon align="inline-start" className={ADDON_CLASS}>
              {renderAdornment(startAdornment)}
            </InputGroupAddon>
          )}
          <InputGroupInput
            ref={ref}
            id={inputId}
            name={name}
            placeholder={placeholderText}
            required={required}
            disabled={disabled}
            aria-invalid={resolvedError ? true : undefined}
            aria-describedby={describedBy}
            onChange={handleInputChange}
            onInput={handleInput}
            onBlur={handleBlur}
            {...props}
          />
          {end && (
            <InputGroupAddon align="inline-end" className={ADDON_CLASS}>
              {renderAdornment(end)}
            </InputGroupAddon>
          )}
        </InputGroup>
        {resolvedDescription && (
          <FieldDescription id={descriptionId}>
            {resolvedDescription}
          </FieldDescription>
        )}
        {hasErrorMessage && (
          <FieldError id={errorId}>{resolvedError}</FieldError>
        )}
      </Field>
    );

    if (disabled && disabledTooltip) {
      return (
        <Tooltip>
          <TooltipTrigger
            render={<div className={cn({ "w-full": fullWidth })} />}
          >
            {field}
          </TooltipTrigger>
          <TooltipContent>{disabledTooltip}</TooltipContent>
        </Tooltip>
      );
    }

    return field;
  },
);

FormInput.displayName = "FormInput";

export { FormInput };
